import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { enableScrolling } from '../redux/slices/uiSlice';
import { scrollAllToTop } from '../utils/scrollToTop';

/**
 * ScrollManager component
 * Handles application-wide scrolling behavior
 * Uses Redux for state management
 */
const ScrollManager = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const prevLocationRef = useRef();
  const scrollPositionsRef = useRef(new Map()); // pathname -> scrollY
  const storageKey = (loc) => `wht:scroll:${(loc?.pathname || '')}|${(loc?.search || '')}|${(loc?.hash || '')}`;

  useEffect(() => {
    const prev = prevLocationRef.current;
    // Save scroll position for the previous path before handling new route
    try {
      if (prev?.pathname) {
        scrollPositionsRef.current.set(prev.pathname, window.scrollY || window.pageYOffset || 0);
        try { sessionStorage.setItem(storageKey(prev), String(window.scrollY || window.pageYOffset || 0)); } catch {}
      }
    } catch {}
    // If only location.state changed (URL is identical) do nothing
    const urlUnchanged = prev &&
      prev.pathname === location.pathname &&
      prev.search === location.search &&
      prev.hash === location.hash;

    if (urlUnchanged) {
      prevLocationRef.current = location;
      return; // Skip auto-scroll logic
    }

    // Check if we have a scroll position to restore
    const savedPos = scrollPositionsRef.current.get(location.pathname);
    let savedPosFromStorage = null;
    try {
      const raw = sessionStorage.getItem(storageKey(location));
      if (raw != null && raw !== '') savedPosFromStorage = Number(raw);
      if (Number.isNaN(savedPosFromStorage)) savedPosFromStorage = null;
    } catch {}
    const shouldRestoreScroll = (typeof savedPos === 'number' || typeof savedPosFromStorage === 'number') || location.hash || 
      location.state?.scrollToSolution || 
      location.state?.scrollToPost;
      
    // Only scroll to top if this is a new navigation and we don't have a specific scroll target
    if (typeof savedPos === 'number' || typeof savedPosFromStorage === 'number') {
      const y = (typeof savedPos === 'number') ? savedPos : savedPosFromStorage || 0;
      const isHomeRoute = location.pathname === '/home' || location.pathname === '/';
      const restoreScroll = () => {
        if (isHomeRoute) {
          window.scrollTo({ top: y, left: 0, behavior: 'instant' });
        } else {
          window.scrollTo(0, y || 0);
        }
      };
      try { requestAnimationFrame(restoreScroll); } catch { restoreScroll(); }
      prevLocationRef.current = location;
      dispatch(enableScrolling());
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
      return;
    }

    if (!shouldRestoreScroll && !location.state?.preserveScroll) {
      // Only scroll to top if we're not handling a custom scroll position
      // and we're not in a state where we want to preserve the scroll position
      const isNavigationFromSolutionView = 
        prevLocationRef.current?.pathname?.startsWith('/solutions/') &&
        !location.pathname.startsWith('/solutions/');
      // Compare top-level sections to avoid scroll resets within same area (e.g., /home -> /home)
      const getTopSection = (path) => {
        try { return String(path || '').split('/')[1] || ''; } catch { return ''; }
      };
      const sameTopSection = prev && getTopSection(prev.pathname) === getTopSection(location.pathname);
      
      if (!isNavigationFromSolutionView && !sameTopSection) {
        // Defer to next frame to let the new route content layout first
        try { requestAnimationFrame(() => scrollAllToTop('auto')); } catch { scrollAllToTop('auto'); }
      }
    }
    
    // Store current location for next comparison
    prevLocationRef.current = location;
    
    // Enable scrolling to allow navigation to work properly
    dispatch(enableScrolling());
    
    // Ensure scrolling is enabled
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    
    return () => {
      // Cleanup if needed
    };
  }, [location, dispatch]);

  // This component doesn't render anything, it just manages scroll behavior
  return null;
};

export default ScrollManager;